/**
 * Minimum value (DOLLARS) for any buyer deposit that will be CHARGED through
 * Stripe (PRO-599).
 *
 * When a deposit settles to a producer's controller-type connected account,
 * the platform recoups the estimated Stripe processing fee (~2.9% + 30¢ of
 * the buyer's total) out of the deposit's settlement via the application fee.
 * A deposit smaller than that charge-out leaves the connected account with a
 * NEGATIVE balance. $25 covers the charge-out with wide margin (it absorbs
 * the processing fee on platform service fees up to ~$800).
 *
 * The floor is MANDATORY wherever a chargeable deposit amount is set —
 * per-species processor deposit settings, producer listing deposits, producer
 * default deposits — and is also clamped at charge/display resolution so
 * legacy below-minimum stamps can never under-collect. Zero/unset stays legal
 * everywhere it is legal today: it means "not priced" (size not claimable) or
 * "no deposit", NOT a small deposit. The producer's direct no-deposit
 * placement path ($0 Order, no Stripe charge) is deliberately exempt.
 */
export const MINIMUM_DEPOSIT_DOLLARS = 25;

/**
 * True when `value` is a positive deposit amount that undercuts the floor —
 * i.e. a value save/publish paths must reject. Null/undefined/0 (and
 * non-finite garbage) return false: "unpriced" is validated elsewhere.
 * @param value
 */
export function isBelowDepositMinimum(
  value: number | null | undefined,
): boolean {
  return (
    typeof value === 'number' &&
    Number.isFinite(value) &&
    value > 0 &&
    value < MINIMUM_DEPOSIT_DOLLARS
  );
}

/** The scheduling-deposit shape both packages read, narrowed to what "paid" needs. */
export interface SchedulingDepositLike {
  amount?: number | null;
  /** Canonical state. New code sets this; legacy embedded-checkout deposits do not. */
  status?: string | null;
  checkoutSession?: { payment_status?: string | null } | null;
}

/**
 * Whether a job's SCHEDULING deposit has actually been paid.
 *
 * Shared because the answer decides whether money is credited off a bill, and
 * two packages ask it: the invoice draft the processor generates (web) and the
 * billing statement PDF (api). They disagreed — the statement recognised only
 * the legacy `checkoutSession.payment_status`, which no modern path writes, so
 * a deposit taken by card or marked paid at the counter was missing from it;
 * the invoice checked nothing at all and credited a deposit that was still
 * OWED, handing the customer money the plant had never received.
 *
 * Both shapes count, because both exist in the data:
 *   `status: 'paid'`                        every path since the deposit
 *                                           methods were introduced
 *   `checkoutSession.payment_status: 'paid'` legacy self-serve embedded checkout
 *
 * `pending` and `failed` are NOT paid. That distinction became load-bearing
 * with wild game: a game job carries on at `Dropped Off` with its deposit
 * still owed (the animal is already hanging — see the phase notes), so an
 * unpaid deposit reaching the invoice is an ordinary state there rather than
 * the near-impossibility it is on the booked path.
 * @param deposit - The job's `deposit` sub-document, if any.
 * @returns True only when the money is actually in.
 */
export function isSchedulingDepositPaid(
  deposit: SchedulingDepositLike | null | undefined,
): boolean {
  if (!deposit) return false;
  return (
    deposit.status === 'paid' ||
    deposit.checkoutSession?.payment_status === 'paid'
  );
}
